import * as React from 'react';
import { ChangeEvent, MouseEvent } from 'react';
import { Button, TextField } from '@origin-digital/style-guide';
import { Box } from '@origin-digital/layout';
import { AuthCard } from '../../../../components/Card';
import { Heading } from '../../../../components/Heading';
import { InfoText } from '../../../../components/InfoText';
import styled from 'styled-components';
import { LG } from '@origin-digital/helper';
import { onlyNumeric } from '@origin-digital/normalize';
import { CodeResendStatus } from './CodeResendStatus';
import { ResendCodeView } from './ResendCodeView';
import { Alert } from '../../../../components/Alert';
import OTCPhone from 'images/otc_phone.svg';
import { UndecoratedLink } from 'ts/components/UndecoratedLink';
import { Page } from '../../../../components/Page';

interface OneTimeCodeViewProps {
  emailOrPhone: string;
  onResendClicked: (event: MouseEvent<HTMLAnchorElement>) => void;
  onFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onOneTimeCodeChanged?: (event: ChangeEvent<HTMLInputElement>) => void;
  showWrongCodeSubmittedMessage: boolean;
  codeResendStatus: CodeResendStatus;
  spinner: boolean;
  showChangeEmailLink: boolean;
}

const OneTimeCodeView: React.FC<OneTimeCodeViewProps> = ({
  emailOrPhone,
  onResendClicked,
  onFormSubmit,
  onOneTimeCodeChanged,
  showWrongCodeSubmittedMessage,
  codeResendStatus,
  spinner,
  showChangeEmailLink,
}) => (
  <Page>
    <AuthCard image={OTCPhone}>
      <form data-id="onetimecode-form" onSubmit={onFormSubmit}>
        {showWrongCodeSubmittedMessage && (
          <Alert text="Hmm, it looks like that code isn't right or has expired. Please try again." />
        )}

        {codeResendStatus === CodeResendStatus.Failed && (
          <Alert text="Sorry, something went wrong when we tried resending your one-time code. Please try again later." />
        )}
        <Heading text="Step 2: Add your code" />
        <InfoText text={`We've sent a one-time code to __${emailOrPhone}__.`} />
        <TextField
          data-id="onetimecode-input"
          onChange={onOneTimeCodeChanged}
          label={'Enter your one-time code'}
          name={'onetimepasscode'}
          transform={onlyNumeric}
          disableIcon={true}
          type={'tel'}
          pattern={'[0-9]*'}
          maxLength={6}
        />
        <ButtonContainer>
          <Button
            data-id="onetimecode-submit"
            type="submit"
            text={'Verify account'}
            block={true}
            spinner={spinner}
          />
        </ButtonContainer>
        <Box display="flex" justifyContent="center">
          <ResendCodeView codeResendStatus={codeResendStatus} onResendClicked={onResendClicked} />
        </Box>
        {showChangeEmailLink && (
          <Box display="flex" justifyContent="center" marginTop={4}>
            <UndecoratedLink
              color="red"
              data-id="change-email-button"
              href="mailto:support@originenergy.com.au"
              text="Need to change your email? Contact us"
            />
          </Box>
        )}
      </form>
    </AuthCard>
  </Page>
);

const ButtonContainer = styled.div`
  margin-bottom: 24px;
  ${LG`
    margin-bottom: 32px;
  `}
`;

export { OneTimeCodeView };
